"use client";

// Package Imports
import React, { useEffect, useState } from "react";
import Link from "next/link";
import { signIn } from 'next-auth/react';
import classNames from "classnames";

// Component Imports
import StylizedButton from "@/components/stylizedButton";

// Context Imports
import { getSession } from 'next-auth/react';
import { useOrder } from '@/lib/context/orderContext';

// Function Imports
import { migrateOrder } from '@/lib/functions/migrateOrder';
import { logMessage } from '@/lib/functions/logMessage';
import {
  getRememberedLoginEmail,
  setRememberedLoginEmail,
} from '@/lib/functions/rememberLoginEmail';

// Style Imports 
import styles from "./cartCheckoutLogin.module.scss";

type Inputs = {
  username: string;
  password: string;
  remember: boolean;
};

interface CartCheckoutLoginProps {
  refreshCart: (showLoader: boolean) => void;
}

export default function CartCheckoutLogin({ refreshCart }: CartCheckoutLoginProps) {
  const { state: orderState } = useOrder();
  const [ isOpen, setIsOpen ] = useState<boolean>(false);
  const [ formData, setFormData ] = useState<Inputs>({ username: "", password: "", remember: false });
  const [ error, setError ] = useState('');
  const [ isLoading, setIsLoading ] = useState(false);

  useEffect(() => {
    const rememberedEmail = getRememberedLoginEmail();
    if (rememberedEmail) {
      setFormData((prev) => ({
        ...prev,
        username: rememberedEmail,
        remember: true,
      }));
    }
  }, []);

  const toggleSection = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsOpen(e.currentTarget.value === "show");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleLogin = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault(); // prevent checkout form submission

    setIsLoading(true);
    setError('');

    let newError = '';

    if (!formData.username || !formData.password) {
      newError = "A username and password are required to log in.";
    }

    if (!newError) {
      try {
        const response = await signIn('credentials', {
          email: formData.username,
          password: formData.password,
          redirect: false,
        });

        if (response?.error) {
          setError(response.status === 401 ? 'Invalid username, email, or password.' : response.error);
          setIsLoading(false);
        } else {
          setRememberedLoginEmail(formData.remember ? formData.username : null);

          // Attempt migrating the order, so that it is 
          // correctly associated with the logged-in user
          try {
            const orderToken = orderState.token;
            const session = await getSession();
            const authToken = session?.token;
  
            if (orderToken && authToken) {
              await migrateOrder(orderToken, authToken);
            }
          } catch (err) {
            console.error('Order migration failed after login:', err);
          }

          await refreshCart(false);

          setIsLoading(false);
        }
      } catch (error) {
        logMessage(`Unexpected login error: ${error}`, 'error');
      }
    } else {
      setError(newError);
    }
  };

  return (
    <section className={styles.cartCheckoutLogin}>
      <div className={styles.cartCheckoutLoginMethod}>
        <div className="formInput formRadio">
          <input type="radio" id="guest-checkout" name="login_toggle" value="hide" onChange={toggleSection} defaultChecked={true} />
          <label htmlFor="guest-checkout">Checkout as Guest</label>
        </div>
        <div className={classNames(styles.customerLogin, "formInput formRadio")}>
          <input type="radio" id="user-checkout" name="login_toggle" value="show" onChange={toggleSection} />
          <label htmlFor="user-checkout">Returning Customer (Login)</label>
        </div>
      </div>

      {isOpen && (
        <div className={styles.cartLoginForm}>
          <div className={styles.cartLoginFormFields}>
            <div className="formInput formText">
              <label htmlFor="username">Username or Email</label>
              <input
                id="username"
                name="username"
                autoComplete="email"
                value={formData.username}
                onChange={handleChange}
              />
              {/* {errors.username && <span className={styles.error}>{errors.username}</span>} */}
            </div>
            <div className="formInput formText">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
              />
              {/* {errors.password && <span className={styles.error}>{errors.password}</span>} */}
            </div>
            <div>
              <StylizedButton
                submitType="button"
                variant="secondary"
                size="medium"
                onClick={handleLogin}
                disabled={isLoading || !formData.username || !formData.password}>
                {isLoading ? 'Logging In...' : 'Log In'}
              </StylizedButton>
            </div>
          </div>
          <div className={styles.cartLoginFormRemember}>
            <div className={styles.checkbox}>
              <label htmlFor="rememberme">Remember Me</label>
              <input
                id="rememberme"
                type="checkbox"
                name="remember"
                checked={formData.remember}
                onChange={handleChange}
              />
            </div>
            <div className={styles.lostPassword}>
              <Link href="/my-account/lost-password/">Lost your Password?</Link>
            </div>
          </div>
          <div className={styles.cartLoginFormError}>
            {!!error && <span>{error}</span>}
          </div>
        </div>
      )}
    </section>
  );
}
